import React, { useEffect, useState, useMemo, useCallback } from "react";
import "../Vocabularies/Crud/ResourceDetail.css";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import FilterSearch from "../../components/Filtering/FilterSearch";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import CustomButton from "../../components/buttons/CustomButton";
import SearchButton from "../../components/buttons/SearchButton";
import DataTable from "../../components/DataTable/DataTable";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { getTextByLang } from "../../utils/formUtils";
import { formatDate } from "../../utils/constants";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { Spinner } from "../../components/Filtering/Reusable/Spinner";
import { useGetCatalog } from "../../hooks/catalogs/useGetCatalog";
import { useDeleteCatalog } from "../../hooks/catalogs/useDeleteCatalog";
import { extractIdFromIri } from "../../utils/extractFromIri";
import OfferDetails from "../OfferingData/OfferingDetails";
import AddIcon from "@mui/icons-material/Add";

function MyDatasets() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    tableData,
    setTableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
    orgProvider,
  } = useTableControls([]);

  const { isProvider } = useAuthRoles();
  const { mutateAsync: deleteCatalog } = useDeleteCatalog({
    callApiWithWorker,
    t,
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const {
    data: pageResponse,
    isLoading,
  } = useGetCatalog({
    callApiWithWorker,
    language: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  useEffect(() => {
    const content = pageResponse?.content ?? [];

    const normalized = content.map((item) => ({
      ...item,
      title: getTextByLang(item.title, i18n.language),
      description: getTextByLang(item.description, i18n.language),
    }));

    setTableData(normalized);
    setRowCount(pageResponse?.totalElements ?? 0);
  }, [pageResponse, i18n.language, setTableData, setRowCount]);

  const handleEdit = useCallback((row) => {
    const iriOrId = row?.id ?? row?.identifier;
    const id = extractIdFromIri(iriOrId);

    setSelectedOfferId(id);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedOfferId(null);
  }, []);

  const handleDelete = async (identifier) => {
    const id = extractIdFromIri(identifier);

    const result = await Swal.fire({
      icon: "warning",
      title: t("dataOfferings.deleteMessage") || "¿Borrar?",
      text:
        t("dataOfferings.deleteSubMessage") || "Esta acción es irreversible",
      showCancelButton: true,
      confirmButtonText: t("common.delete") || "Delete",
      cancelButtonText: t("common.cancel") || "Cancel",
    });

    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      await deleteCatalog({ id });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("dataOfferings.offerings.dict.lastUpdate"),
        accessorKey: "updated_at",
        meta: { width: "10%" },
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        header: t("dataOfferings.offerings.dict.name"),
        accessorKey: "title",
        meta: { width: "45%" },
      },
      {
        header: t("dataOfferings.offerings.dict.description"),
        accessorKey: "description",
        meta: { width: "45%" },
      },
    ],
    [t],
  );

  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex">
        <div className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("privateArea.mydatasets.title")}</h2>
          {isProvider && (
            <CustomButton
              onClick={() => navigate("/offeringdata")}
              startIcon={<AddIcon />}
            >
              {t("dataModels.createofferingbutton")}
            </CustomButton>
          )}
        </div>
        <div className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              columns={columns}
              data={filteredData}
              showActions={orgProvider}
              initialSorting={[{ id: "updated_at", desc: true }]}
              onEdit={handleEdit}
              onDelete={(row) => handleDelete(row.id)}
              onRowClick={handleEdit}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
              loading={isLoading}
            />
          </div>
        </div>
        <OfferDetails
          open={detailsOpen}
          onClose={handleCloseDetails}
          offerId={selectedOfferId}
          callApiWithWorker={callApiWithWorker}
          isProvider={isProvider}
        />
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </section>
  );
}

export default MyDatasets;
